import type { TKey } from './i18n';
import type { RunRecord } from './runHistory';

export type AchievementProgress = { current: number; target: number };

export type AchievementDef = {
  id: string;
  icon: string;
  nameKey: TKey;
  descKey: TKey;
  unlocked: (records: RunRecord[]) => boolean;
  /** Optional — return current/target for a progress bar. Omit for boolean achievements. */
  progress?: (records: RunRecord[]) => AchievementProgress;
};

export const ACHIEVEMENTS: ReadonlyArray<AchievementDef> = [
  {
    id: 'first_kill',
    icon: '⚔️',
    nameKey: 'ach.firstKill.name',
    descKey: 'ach.firstKill.desc',
    unlocked: (r) => r.some((x) => x.kills > 0),
  },
  {
    id: 'first_extract',
    icon: '◆',
    nameKey: 'ach.firstExtract.name',
    descKey: 'ach.firstExtract.desc',
    unlocked: (r) => r.some((x) => x.outcome === 'won'),
  },
  {
    id: 'ten_extracts',
    icon: '🏆',
    nameKey: 'ach.tenExtracts.name',
    descKey: 'ach.tenExtracts.desc',
    unlocked: (r) => r.filter((x) => x.outcome === 'won').length >= 10,
    progress: (r) => ({
      current: Math.min(10, r.filter((x) => x.outcome === 'won').length),
      target: 10,
    }),
  },
  {
    id: 'hundred_kills',
    icon: '💀',
    nameKey: 'ach.hundredKills.name',
    descKey: 'ach.hundredKills.desc',
    unlocked: (r) => r.reduce((sum, x) => sum + x.kills, 0) >= 100,
    progress: (r) => ({
      current: Math.min(100, r.reduce((sum, x) => sum + x.kills, 0)),
      target: 100,
    }),
  },
  {
    id: 'full_clear',
    icon: '✨',
    nameKey: 'ach.fullClear.name',
    descKey: 'ach.fullClear.desc',
    unlocked: (r) => r.some((x) => x.outcome === 'won' && x.kills >= 30),
  },
  {
    id: 'collector',
    icon: '🧬',
    nameKey: 'ach.collector.name',
    descKey: 'ach.collector.desc',
    unlocked: (r) => {
      const all = new Set<string>();
      r.forEach((x) => x.perks.forEach((p) => all.add(p)));
      return all.size >= 5;
    },
    progress: (r) => {
      const all = new Set<string>();
      r.forEach((x) => x.perks.forEach((p) => all.add(p)));
      return { current: Math.min(5, all.size), target: 5 };
    },
  },
  {
    id: 'survivor',
    icon: '🐾',
    nameKey: 'ach.survivor.name',
    descKey: 'ach.survivor.desc',
    unlocked: (r) => r.some((x) => x.outcome === 'won' && x.durationMs >= 5 * 60 * 1000),
  },
  {
    id: 'veteran',
    icon: '⭐',
    nameKey: 'ach.veteran.name',
    descKey: 'ach.veteran.desc',
    unlocked: (r) => r.length >= 25,
    progress: (r) => ({
      current: Math.min(25, r.length),
      target: 25,
    }),
  },
];

/** Set of achievement IDs currently unlocked for the given history. */
export function unlockedSet(records: RunRecord[]): Set<string> {
  const result = new Set<string>();
  for (const a of ACHIEVEMENTS) {
    if (a.unlocked(records)) result.add(a.id);
  }
  return result;
}
